import { FC, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import Helmet from '@/components/meta/Helmet';
import GuideVotingTemplate from '@/components/templates/Guide/Voting';
import { RACE_TYPE_PARAM_KEY } from '@/config/constants/query';
import { useSetBreadcrumbContext } from '@/contexts/breadcrumb';
import { useLayoutContext } from '@/contexts/layout';
import { RaceType } from '@/modelTypes';

import { GuidePageTitles } from '../types';

const GuideVotingPage: FC = () => {
  const { setLayoutStyles } = useLayoutContext();
  const setBreadcrumbs = useSetBreadcrumbContext();

  useLayoutEffect(() => {
    setLayoutStyles();
  }, []);

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const paramType = queryParams.get(RACE_TYPE_PARAM_KEY);

  const [raceType, setRaceType] = useState<RaceType>(
    paramType === RaceType.Multirace ? RaceType.Multirace : RaceType.Singlerace,
  );

  const title = useMemo(() => {
    const base = GuidePageTitles.Voting;
    switch (raceType) {
      case RaceType.Singlerace: {
        return `${base} 競輪投票`;
      }
      case RaceType.Multirace: {
        return `${base} チャリロト・Dokanto!`;
      }
      default: {
        return base;
      }
    }
  }, [raceType]);

  useEffect(() => {
    queryParams.set(RACE_TYPE_PARAM_KEY, raceType);
    navigate(`${location.pathname}?${queryParams.toString()}`, {
      state: {
        cancelScroll: true,
      },
      replace: true,
    });

    setBreadcrumbs([
      {
        name: 'ご利用ガイド',
        to: '/guide',
      },
      {
        name: `投票方法（${
          raceType === RaceType.Singlerace ? '競輪' : 'チャリロト・Dokanto!'
        }）`,
        to: '',
      },
    ]);
  }, [raceType]);

  const description = useMemo(() => {
    const base = 'DMM競輪のご利用ガイドページ。';
    switch (raceType) {
      case RaceType.Singlerace: {
        return `${base}競輪投票の投票ページの使い方をご案内します。`;
      }
      case RaceType.Multirace: {
        return `${base}チャリロトとDokanto!の投票方法をご案内しています。`;
      }
      default: {
        return base;
      }
    }
  }, [raceType]);

  return (
    <>
      <Helmet
        meta={{
          title,
          description,
          keywords: `${GuidePageTitles.Voting},ご利用ガイド,%s`,
        }}
      />
      <GuideVotingTemplate raceType={raceType} setRaceType={setRaceType} />
    </>
  );
};

export default GuideVotingPage;
